'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from 'next/navigation';
import { useForm } from 'react-hook-form';
import type { z } from 'zod';

import type { SocialAuthProviderItem } from '@eliadmin/core/src/api/customers/customer-social-auth-api';

import { signIn } from '@eliadmin/core/src/api/customers/customers-api';
import { ValidationError } from '@eliadmin/core/src/api/errors';
import { signInSchema } from '@eliadmin/core/src/components/auth/sign-in-schema';
import { SocialAuthButtons } from '@eliadmin/core/src/components/auth/social-auth-buttons';
import Link from '@eliadmin/core/src/components/link';
import {
  CaptchaAction,
  useRecaptcha,
} from '@eliadmin/core/src/components/providers/captcha-provider';
import { PasswordField, TextField } from '@eliadmin/core/src/components/svetech/forms/fields';
import {
  AuthHeader,
  textLinkClassName,
} from '@eliadmin/core/src/components/svetech/forms/form-parts';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { DialogClose } from '@eliadmin/core/src/components/ui/dialog';
import { Form, FormField, FormItem, FormMessage } from '@eliadmin/core/src/components/ui/form';
import { useToast } from '@eliadmin/core/src/components/ui/use-toast';
import { AUTH_CONFIG } from '@eliadmin/core/src/constants/auth-config';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { setAuthToken } from '@eliadmin/core/src/server/api-client';
import { initCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { cn } from '@eliadmin/core/src/utils/style-utils';

export { signInSchema } from '@eliadmin/core/src/components/auth/sign-in-schema';

type SignInFormProps = {
  initialSocialAuthProviders?: SocialAuthProviderItem[];
  inDialog?: boolean;
};

/**
 * Sign-in (page and dialog; core logic unchanged): H2 title with the registration prompt, social
 * sign-in, e-mail and password, the full-width submit and the green „forgotten password“ link.
 */
export function SignInForm({ initialSocialAuthProviders, inDialog }: SignInFormProps) {
  const { t } = useTranslations();

  const { formValidationMessages } = useFormValidationMessages(t);
  const schema = signInSchema(formValidationMessages);

  const form = useForm<z.input<typeof schema>, any, z.output<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
      password: '',
      'g-recaptcha-response': '',
    },
  });

  const { toast } = useToast();
  const locale = useLocaleStore(s => s.currentLocale);
  const router = useRouter();

  const { executeRecaptcha } = useRecaptcha();

  const handleSubmit = form.handleSubmit(async data => {
    try {
      let captchaRes = undefined;
      if (executeRecaptcha) {
        captchaRes = await executeRecaptcha(CaptchaAction.LOGIN);
      }
      const resp = await signIn({ ...data, 'g-recaptcha-response': captchaRes!, locale });
      await setAuthToken({ authToken: resp.data.token });
      initCustomerStore({
        authToken: resp.data.token,
        customer: resp.data.customer,
      });
      toast({
        title: t('text656'),
        variant: 'success',
      });
      router.replace(ROUTES.PROFILE());
    } catch (e) {
      // handle 422
      if (e instanceof ValidationError) {
        for (const [field, errors] of Object.entries(e.fields)) {
          form.setError(field as any, { type: 'manual', message: errors.join('\n') });
        }
        return;
      }

      toast({
        title: t('text594'),
        description: (e as any)?.message,
        variant: 'destructive',
      });
    }
  });

  const registerLink = (
    <Link
      href={ROUTES.REGISTER() ?? '#'}
      className={cn(textLinkClassName, 'ea-sign-in-form__header-link')}
      prefetch={false}
    >
      {t('text621')}
    </Link>
  );

  return (
    <Form {...form}>
      <form
        noValidate
        className={cn('flex w-full flex-col gap-6 md:gap-8', 'ea-sign-in-form')}
        onSubmit={handleSubmit}
      >
        <div className="ea-sign-in-form__header">
          <AuthHeader title={t('text190')} as={inDialog ? 'h2' : 'h1'}>
            {t('text620')} {/* In the dialog the link also closes it (core behaviour). */}
            {inDialog ? <DialogClose asChild>{registerLink}</DialogClose> : registerLink}.
          </AuthHeader>
        </div>

        <SocialAuthButtons
          className="ea-sign-in-form__social-auth w-full"
          initialProviders={initialSocialAuthProviders}
        />

        <div className="ea-sign-in-form__content flex flex-col gap-4">
          <TextField
            control={form.control}
            name="email"
            type="email"
            autoComplete="email"
            label={t('text35')}
            placeholder={t('text259')}
            required
            className="ea-sign-in-form__email-field"
          />
          <PasswordField
            control={form.control}
            name="password"
            autoComplete="current-password"
            label={t('text191')}
            placeholder={t('text191')}
            required
            className="ea-sign-in-form__password-field"
          />
          <FormField
            control={form.control}
            name="g-recaptcha-response"
            render={() => (
              <FormItem className="w-full empty:hidden">
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="ea-sign-in-form__footer flex flex-col items-center gap-4">
          <Button
            type="submit"
            className="ea-sign-in-form__submit-button w-full"
            isLoading={form.formState.isSubmitting}
          >
            {t('text189')}
          </Button>
          {AUTH_CONFIG.signIn.passwordResetPosition === 'inside' && (
            <Link
              className={cn('svt-body', textLinkClassName, 'ea_sign-in-form__password-reset-link')}
              href={ROUTES.PASSWORD_RESET() ?? '#'}
              prefetch={false}
            >
              {t('text188')}
            </Link>
          )}
        </div>
      </form>
    </Form>
  );
}
